/**
 * GA4 Analytics Utility
 * Centralised helpers for all custom GA4 events.
 * All functions are safe to call server-side (SSR) - they guard against
 * missing window/gtag before firing.
 *
 * Section tracking events (added Session 23):
 *   section_cta_click  - user clicks a CTA inside a named homepage section
 *   section_view       - IntersectionObserver fires when section enters viewport
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    oaiq?: (...args: unknown[]) => void;
    _isEuUk?: boolean;
  }
}

/** Whether analytics events may be sent under the site's published consent model. */
function hasAnalyticsMeasurementConsent(): boolean {
  if (typeof window === "undefined") return false;

  try {
    const analytics = localStorage.getItem("cookie-consent-analytics");
    // index.html and CookieConsent both set analytics_storage to granted by
    // default. A stored "denied" value is the visitor's explicit opt-out.
    return analytics !== "denied";
  } catch {
    // The head's consent default remains granted when localStorage is blocked.
    return true;
  }
}

/**
 * Queue a site-owned, non-PII envelope for one GTM forwarding tag.
 *
 * GTM's Google tag owns page measurement, but it does not automatically turn
 * application `gtag("event", ...)` command tuples into GA4 custom events. The
 * marker gives the one allow-listed forwarding tag a stable trigger without
 * sending emails, submitted sites, query strings or free-text form responses.
 */
function queueGtmAnalyticsEvent(
  eventName: string,
  params: Record<string, unknown>
): void {
  // GTM's Google tag already sends the initial page view, while the Router sends
  // later SPA views directly. Forwarding either through the custom-event bridge
  // would double-count page views and distort engagement time.
  if (eventName === "page_view") return;
  if (typeof window === "undefined" || !Array.isArray(window.dataLayer)) return;

  window.dataLayer.push({
    event: "integrated_ga4_event",
    integrated_ga4_event_source: "site_analytics_helper_v1",
    integrated_ga4_event_name: eventName,
    ...params,
  });
}

/** Fire a GA4 event only when the visitor has not opted out of analytics. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function gtagEvent(eventName: string, params: Record<string, any>): void {
  if (!hasAnalyticsMeasurementConsent()) return;
  if (typeof window === "undefined") return;

  // GTM owns the production Google tag. Route application custom events only
  // through its allow-listed bridge, rather than also calling gtag directly:
  // the latter is not reliably collected by this container and would duplicate
  // any event that is forwarded successfully. SPA page views are the one
  // exception, because the bridge explicitly omits them to prevent duplicate
  // page measurement and distorted engagement time.
  if (eventName !== "page_view") {
    queueGtmAnalyticsEvent(eventName, params);
    return;
  }

  if (typeof window.gtag === "function") {
    window.gtag("event", eventName, params);
  }
}

/** Backward-compatible named gate for helpers that document consent explicitly. */
function consentedGtagEvent(
  eventName: string,
  params: Record<string, any>
): void {
  gtagEvent(eventName, params);
}

/**
 * Score-funnel lifecycle event. Deliberately accepts only the public source
 * path: never pass email, domain, name, score, OTP, referrer or URL values.
 * The stable form_name lets GA4 funnel explorations isolate this score journey
 * from other site forms without collecting visitor-identifying data.
 */
export type ScorePlacement =
  | "home_hero"
  | "blog_end"
  | "service_hero"
  | "service_end"
  | "header"
  | "sticky_mobile"
  | "local_hub_hero"
  | "local_hub_end"
  | "local_hero"
  | "local_end"
  | "local_sw_hero"
  | "local_sw_end"
  | "local_sticky"
  | "audit_page"
  | "hero"
  | "ecommerce_ai_marketing"
  | "ecommerce_ai_marketing_end"
  | "citation_hero"
  | "citation_end"
  | "ecommerce_hero"
  | "ecommerce_end";

export function trackScoreEvent(
  eventName:
    | "score_start"
    | "score_url_accepted"
    | "score_preview_complete"
    | "score_email_step"
    | "score_lead_saved"
    | "score_code_sent"
    | "score_verified"
    | "score_pdf",
  params: { source_page: string; placement?: ScorePlacement }
): void {
  consentedGtagEvent(eventName, {
    form_name: "ai_visibility_score",
    source_page: params.source_page.slice(0, 512),
    ...(params.placement ? { placement: params.placement } : {}),
    ...getFormAttribution(),
  });
}

/**
 * Final-complete score entry events. This narrow helper deliberately accepts no
 * visitor-entered values, URLs, email addresses, names, scores, UTM values or
 * Calendly URIs. It is used by the two new score-first service journeys while
 * their existing generic score telemetry remains backwards compatible.
 */
import type { ScoreNextIntent, ScoreSourcePlacement } from "./scoreJourney";

export type ScoreBookingSource =
  | "hero_link"
  | "results_card"
  | "report_email"
  | "unknown";

type ScoreFlowEventName =
  | "score_start"
  | "lead_form_submit"
  | "generate_lead"
  | "score_step_1"
  | "score_verified"
  | "booking_click"
  | "booking_complete";

type ScoreFlowEventParams = {
  form_id?: "score";
  placement?: ScoreSourcePlacement;
  next_intent: ScoreNextIntent;
  page_type?: "service";
  niche?: "ai_citation_programme";
  form_position?: "inline" | "audit_page";
  lead_type?: "ai_visibility_score";
  event_id?: string;
  lead_id?: number;
  value?: number;
  currency?: "GBP";
  booking_source?: ScoreBookingSource;
};

export function trackScoreFlowEvent(
  eventName: ScoreFlowEventName,
  params: ScoreFlowEventParams
): void {
  consentedGtagEvent(eventName, {
    form_id: "score",
    page_path: typeof window === "undefined" ? "" : window.location.pathname,
    ...params,
    ...getFormAttribution(),
  });
}

const SCORE_BOOKING_CONTEXT_KEY = "score_booking_context";
const SCORE_BOOKING_COMPLETE_KEY = "is_bk";

type ScoreBookingContext = Pick<
  ScoreFlowEventParams,
  "placement" | "next_intent" | "page_type" | "niche" | "lead_id"
> & {
  booking_source: ScoreBookingSource;
};

/** Store only allowlisted, non-PII booking context for Calendly's completion message. */
export function trackScoreBookingClick(context: ScoreBookingContext): void {
  if (typeof window !== "undefined") {
    try {
      window.sessionStorage.setItem(SCORE_BOOKING_CONTEXT_KEY, JSON.stringify(context));
      window.sessionStorage.removeItem(SCORE_BOOKING_COMPLETE_KEY);
    } catch {
      // Analytics remains optional when session storage is unavailable.
    }
  }
  trackScoreFlowEvent("booking_click", context);
}

/**
 * Emits at most one key booking event after Calendly's documented scheduled
 * message. Event and stored context intentionally exclude Calendly URIs, UTMs
 * and all visitor-entered fields.
 */
export function trackScoreBookingComplete(): void {
  if (typeof window === "undefined") return;
  try {
    if (window.sessionStorage.getItem(SCORE_BOOKING_COMPLETE_KEY) === "1") return;
    const parsed = JSON.parse(
      window.sessionStorage.getItem(SCORE_BOOKING_CONTEXT_KEY) ?? "{}"
    ) as Partial<ScoreBookingContext>;
    const placement = parsed.placement;
    const next_intent = parsed.next_intent === "citation" ? "citation" : "none";
    const booking_source: ScoreBookingSource =
      parsed.booking_source === "hero_link" ||
      parsed.booking_source === "results_card" ||
      parsed.booking_source === "report_email"
        ? parsed.booking_source
        : "unknown";
    const event_id = `bk_${window.crypto?.randomUUID?.() ?? Date.now().toString(36)}`;
    window.sessionStorage.setItem(SCORE_BOOKING_COMPLETE_KEY, "1");
    trackScoreFlowEvent("booking_complete", {
      placement,
      next_intent,
      ...(parsed.page_type === "service" ? { page_type: "service" as const } : {}),
      ...(parsed.niche === "ai_citation_programme"
        ? { niche: "ai_citation_programme" as const }
        : {}),
      ...(typeof parsed.lead_id === "number" ? { lead_id: parsed.lead_id } : {}),
      booking_source,
      event_id,
    });
    trackOpenAiAppointmentScheduled();
  } catch {
    // A malformed session value cannot block an independent Calendly booking.
  }
}

/** Whether an advertising measurement event may be sent under the site's consent model. */
function hasAdvertisingMeasurementConsent(): boolean {
  if (typeof window === "undefined") return false;

  // The shared head initialises this before application code. Non-EU/UK visitors
  // follow the existing advertising opt-out model; EU/UK visitors need an
  // explicit advertising choice.
  if (window._isEuUk === false) return true;

  try {
    const consent = localStorage.getItem("cookie-consent");
    const advertising = localStorage.getItem("cookie-consent-advertising");
    return (
      consent === "all" || (consent === "custom" && advertising === "granted")
    );
  } catch {
    return false;
  }
}

/** Fire an advertising conversion only where the visitor granted that category. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function advertisingGtagEvent(
  eventName: string,
  params: Record<string, any>
): void {
  if (!hasAdvertisingMeasurementConsent()) return;
  if (typeof window === "undefined" || typeof window.gtag !== "function")
    return;
  window.gtag("event", eventName, params);
}

type OpenAiAdvertisingConversion = "lead_created" | "appointment_scheduled";

/**
 * Sends a static OpenAI advertising conversion after the caller has established
 * a real business outcome. Payloads intentionally contain no visitor-entered
 * values, identifiers, URLs, attribution parameters, scores, or free text.
 */
function trackOpenAiAdvertisingConversion(
  eventName: OpenAiAdvertisingConversion
): void {
  if (!hasAdvertisingMeasurementConsent()) return;

  const measureConversion = () => {
    // Consent can be withdrawn during the short SDK-initialisation window.
    if (
      !hasAdvertisingMeasurementConsent() ||
      typeof window.oaiq !== "function"
    )
      return;
    window.oaiq("measure", eventName, {
      type: "customer_action",
    });
  };

  if (typeof window.oaiq === "function") {
    measureConversion();
    return;
  }

  // The shared head dispatches this once its consent-gated queue is ready.
  window.addEventListener("openai-advertising-pixel-ready", measureConversion, {
    once: true,
  });
}

/** Records a qualified lead only after the verified score report was delivered. */
export function trackOpenAiAuditPdfDelivery(): void {
  trackOpenAiAdvertisingConversion("lead_created");
}

/** Records an appointment only after Calendly reports its documented scheduled event. */
export function trackOpenAiAppointmentScheduled(): void {
  trackOpenAiAdvertisingConversion("appointment_scheduled");
}

// ---------------------------------------------------------------------------
// Homepage section tracking (Session 23)
// ---------------------------------------------------------------------------

import { useEffect, useRef, useState } from "react";

export interface SectionCtaClickParams {
  /** Slug-style section identifier matching the section id= attribute, e.g. "proof-block" */
  section_name: string;
  /** Visible button / link text, e.g. "Get my free score →" */
  cta_text: string;
  /** Destination path or URL */
  destination: string;
}

/**
 * Fired when a user clicks any CTA within a named homepage section.
 * Maps directly to a GA4 custom event: section_cta_click
 */
export function trackSectionCtaClick(params: SectionCtaClickParams): void {
  gtagEvent("section_cta_click", params);
}

// ---------------------------------------------------------------------------
// Sitewide announcement tracking
// ---------------------------------------------------------------------------

/**
 * Measures the sitewide announcement without collecting visitor data, page URLs,
 * article URLs, query strings, or free-text content. This lets the team compare
 * the announcement CTA against dismissals without creating a visitor profile.
 */
export function trackSitewideAnnouncementInteraction(params: {
  announcement_id: "aeo-citation-panel-operating-sheet-2026-10-29";
  action: "cta_click" | "dismiss";
}): void {
  gtagEvent("sitewide_announcement_interaction", {
    event_category: "sitewide_announcement",
    ...params,
  });
}

/**
 * useSectionView - React hook that fires a section_view GA4 event once when
 * the referenced element scrolls into view (50% threshold, fires only once).
 *
 * Usage:
 *   const ref = useSectionView("proof-block");
 *   <section ref={ref as React.RefObject<HTMLDivElement>} id="proof-block">…</section>
 */
export function useSectionView(
  sectionName: string
): React.RefObject<HTMLElement | null> {
  const ref = useRef<HTMLElement | null>(null);
  const fired = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || fired.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !fired.current) {
          fired.current = true;
          gtagEvent("section_view", { section_name: sectionName });
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [sectionName]);

  return ref;
}

// ---------------------------------------------------------------------------
// Lead generation events
// ---------------------------------------------------------------------------

type FormAttribution = {
  /** Public canonical-like page URL; query strings and fragments are excluded. */
  form_page_url: string;
  /** Coarse acquisition channel retained only for the current browser session. */
  form_user_source:
    | "google"
    | "bing"
    | "linkedin"
    | "meta"
    | "x"
    | "email"
    | "campaign"
    | "referral"
    | "direct"
    | "unknown";
};

const FORM_SOURCE_SESSION_KEY = "integrated_social_form_source_v1";

function getPublicPageUrl(): string {
  if (typeof window === "undefined") return "";
  try {
    const pageUrl = new URL(window.location.href);
    return `${pageUrl.origin}${pageUrl.pathname}`;
  } catch {
    return window.location.pathname || "";
  }
}

function classifyFormSource(
  value: string
): FormAttribution["form_user_source"] {
  const source = value.trim().toLowerCase();
  if (!source) return "unknown";
  if (source.includes("google")) return "google";
  if (source.includes("bing")) return "bing";
  if (source.includes("linkedin")) return "linkedin";
  if (
    source.includes("facebook") ||
    source.includes("instagram") ||
    source.includes("meta")
  )
    return "meta";
  if (source === "x" || source.includes("twitter")) return "x";
  if (source.includes("email") || source.includes("newsletter")) return "email";
  return "campaign";
}

/**
 * Return only non-PII conversion context. The page URL deliberately omits
 * query/hash values and user source is reduced to a coarse channel, never a
 * referrer path, UTM value, email address, submitted website or free text.
 */
function getFormAttribution(): FormAttribution {
  if (typeof window === "undefined") {
    return { form_page_url: "", form_user_source: "unknown" };
  }

  const form_page_url = getPublicPageUrl();
  let currentSource: FormAttribution["form_user_source"] = "direct";

  try {
    const pageUrl = new URL(window.location.href);
    const utmSource = pageUrl.searchParams.get("utm_source");
    if (utmSource) currentSource = classifyFormSource(utmSource);
  } catch {
    /* Retain the direct-source fallback. */
  }

  if (currentSource === "direct") {
    try {
      const referrer = document.referrer ? new URL(document.referrer) : null;
      if (referrer && referrer.origin !== window.location.origin) {
        currentSource =
          classifyFormSource(referrer.hostname) === "campaign"
            ? "referral"
            : classifyFormSource(referrer.hostname);
      }
    } catch {
      // Keep the direct fallback when the browser withholds or rejects referrer data.
    }
  }

  try {
    const savedSource = window.sessionStorage.getItem(
      FORM_SOURCE_SESSION_KEY
    ) as FormAttribution["form_user_source"] | null;
    if (savedSource) return { form_page_url, form_user_source: savedSource };
    window.sessionStorage.setItem(FORM_SOURCE_SESSION_KEY, currentSource);
  } catch {
    // Session storage is optional: retain the current safe source for this event.
  }

  return { form_page_url, form_user_source: currentSource };
}

function trackFormLifecycleEvent(
  eventName:
    | "form_start"
    | "form_submit"
    | "form_success"
    | "form_error"
    | "form_step_complete"
    | "form_otp_verified",
  params: Record<string, string | number | undefined>
): void {
  // gtag queues this complete, enriched payload to dataLayer without a duplicate
  // manual push that could cause GTM to fire the same event twice.
  gtagEvent(eventName, { ...params, ...getFormAttribution() });
}

/** Fired when the hero mini-form (email + URL capture) is successfully submitted */
export function trackHeroFormSubmit(email: string) {
  gtagEvent("generate_lead", {
    event_category: "lead_form",
    event_label: "hero_mini_form",
    // GA4 recommended param - strip domain for privacy
    value: 1,
    ...getFormAttribution(),
  });
  // Google Ads conversion - replace CONVERSION_LABEL with your actual label
  // from Google Ads > Goals > Conversions
  advertisingGtagEvent("conversion", {
    send_to: "AW-667673408",
    event_category: "lead_form",
    event_label: "hero_mini_form",
    ...getFormAttribution(),
  });
}

/**
 * Records the one qualified free-score conversion only after a verified request
 * reaches the completed report state. URL preview, email capture, OTP delivery,
 * OTP verification and PDF download are deliberately separate funnel events.
 */
export function trackVerifiedScoreReportIssued(params: {
  placement: "hero" | "blog_end" | "service_end" | "header" | "audit_page";
}): void {
  consentedGtagEvent("generate_lead", {
    event_category: "lead_form",
    event_label: "verified_score_report_issued",
    form_name: "ai_visibility_score",
    placement: params.placement,
    value: 5,
    ...getFormAttribution(),
  });
}

// ---------------------------------------------------------------------------
// Outbound / engagement events
// ---------------------------------------------------------------------------

/**
 * Fired when a user clicks a Calendly booking link.
 * @param source  Human-readable label for where the link appeared, e.g. "services_page_ai_training"
 */
export function trackCalendlyClick(source: string) {
  gtagEvent("calendly_click", {
    event_category: "outbound",
    event_label: source,
    outbound_url: "https://calendly.com/modielnadi/30min",
  });
  // Also fire as a standard outbound_click so it shows in GA4 engagement reports
  gtagEvent("click", {
    event_category: "outbound",
    link_url: "https://calendly.com/modielnadi/30min",
    link_text: source,
  });
}

// ---------------------------------------------------------------------------
// Arabic regional landing-page booking intent (Session 125)
// ---------------------------------------------------------------------------

/**
 * Fires when a visitor opens the direct Arabic UAE/Saudi consultation booking
 * flow. This measures booking intent only; it must not be interpreted as a
 * confirmed Calendly appointment.
 */
export function trackArabicBookingCtaClick(): void {
  const source = "arabic_agentic_uae_saudi_booking_widget";

  trackCalendlyClick(source);
  gtagEvent("arabic_booking_cta_click", {
    content_language: "ar",
    market_intent: "uae_saudi",
    translation_group_id: "landing-agentic-ai-uae-saudi",
    booking_surface: "direct_widget",
  });
}

// ---------------------------------------------------------------------------
// FAQ accordion tracking (Session 25)
// ---------------------------------------------------------------------------

/**
 * Fired when a user opens or closes an FAQ accordion item.
 * Creates a GA4 custom event: faq_toggle
 * Dimensions: faq_question (first 100 chars), faq_action ("open" | "close"), faq_page
 */
export function trackFaqToggle(params: {
  /** The FAQ question text (truncated to 100 chars for GA4 dimension limits) */
  faq_question: string;
  /** Whether the accordion is being opened or closed */
  faq_action: "open" | "close";
  /** Page slug or identifier, e.g. "faq_page", "service_seo", "service_ppc" */
  faq_page: string;
}): void {
  gtagEvent("faq_toggle", {
    faq_question: params.faq_question.slice(0, 100),
    faq_action: params.faq_action,
    faq_page: params.faq_page,
  });
}

// ---------------------------------------------------------------------------
// Outbound social link tracking (Session 25)
// ---------------------------------------------------------------------------

/**
 * Fired when a user clicks an outbound social media link (LinkedIn, Twitter/X, etc.).
 * Creates a GA4 custom event: social_link_click
 * Dimensions: social_platform, link_location, destination_url
 */
export function trackOutboundSocialClick(params: {
  /** Social platform name, e.g. "linkedin", "twitter", "facebook" */
  social_platform: string;
  /** Where on the page the link appeared, e.g. "header", "footer", "blog_share_row" */
  link_location: string;
  /** Full destination URL */
  destination_url: string;
}): void {
  gtagEvent("social_link_click", params);
  // Also fire as a standard outbound_click for GA4 engagement reports
  gtagEvent("click", {
    event_category: "outbound",
    link_url: params.destination_url,
    link_text: params.social_platform,
  });
}

// ---------------------------------------------------------------------------
// Mobile navigation tracking (Session 29)
// ---------------------------------------------------------------------------

/**
 * Fired for every meaningful mobile nav interaction:
 *   - burger open/close
 *   - accordion expand/collapse
 *   - link tap (any link inside the mobile overlay)
 *   - CTA tap (Free AI Visibility Score, Book a call)
 *
 * Creates a GA4 custom event: mobile_nav
 * Dimensions: nav_action, nav_label, nav_href, nav_section
 */
export function trackMobileNav(params: {
  /** Type of interaction */
  action:
    | "open"
    | "close"
    | "link_tap"
    | "accordion_expand"
    | "accordion_collapse"
    | "cta_tap";
  /** Visible link label or section name */
  label?: string;
  /** Destination URL (for link_tap and cta_tap) */
  href?: string;
  /** Section the link belongs to: core_services | services | resources | static | cta */
  section?: string;
}): void {
  gtagEvent("mobile_nav", {
    nav_action: params.action,
    nav_label: params.label ?? "",
    nav_href: params.href ?? "",
    nav_section: params.section ?? "",
  });
}

// ---------------------------------------------------------------------------
// Tools section event tracking (Session 41)
// ---------------------------------------------------------------------------

/**
 * Fired when a user switches between tools in the PPC Keyword Processor
 * (or any future tool page). Creates a GA4 custom event: tool_tab_switch
 * Dimensions: tool_name, tool_page
 */
export function trackToolTabSwitch(params: {
  /** Tool identifier, e.g. "wrapper", "mixer", "cleaner", "negative", "skag" */
  tool_name: string;
  /** Page slug, e.g. "ppc-keyword-processor" */
  tool_page: string;
}): void {
  gtagEvent("tool_tab_switch", params);
}

/**
 * Fired when a user copies output from a tool.
 * Creates a GA4 custom event: tool_copy
 * Dimensions: tool_name, tool_page, keyword_count
 */
export function trackToolCopy(params: {
  tool_name: string;
  tool_page: string;
  /** Number of keywords/lines in the output that was copied */
  keyword_count: number;
}): void {
  gtagEvent("tool_copy", params);
}

/**
 * Fired when a visitor creates a shareable, client-side tool scenario URL.
 * The event deliberately records no scenario values or personal form data.
 */
export function trackToolShare(params: {
  tool_name: string;
  tool_page: string;
  share_method: "native" | "copy_link";
  scenario_type: string;
}): void {
  gtagEvent("tool_share", params);
}

/**
 * Fired when a user downloads output from a tool as a file.
 * Creates a GA4 custom event: tool_download
 * Dimensions: tool_name, tool_page, keyword_count, file_format
 */
export function trackToolDownload(params: {
  tool_name: string;
  tool_page: string;
  keyword_count: number;
  /** e.g. "txt", "csv" */
  file_format: string;
}): void {
  gtagEvent("tool_download", params);
}

/**
 * Fired when a user processes/generates output in a tool.
 * Creates a GA4 custom event: tool_process
 * Dimensions: tool_name, tool_page, input_count, output_count
 */
export function trackToolProcess(params: {
  tool_name: string;
  tool_page: string;
  /** Number of input keywords */
  input_count: number;
  /** Number of output keywords generated */
  output_count: number;
}): void {
  gtagEvent("tool_process", params);
}

/**
 * Fired on every SPA route change.
 * Called from the Router component in App.tsx.
 *
 * SESSION FIX (Session 41): Uses gtag("event", "page_view") instead of
 * gtag("config") to prevent session splitting and preserve organic attribution.
 *
 * ROOT CAUSE of the GA4 organic traffic discrepancy:
 * The previous implementation called gtag("config", "G-V0XL3F6ZZ9") on every
 * SPA navigation. Calling gtag("config") starts a NEW GA4 session, which
 * resets the traffic source attribution. A visitor arriving from Google organic
 * would have their first page view correctly attributed to organic, but every
 * subsequent SPA navigation would start a new session attributed to (direct).
 * Result: only 1 organic session despite 100+ active users.
 *
 * THE FIX: gtag("event", "page_view") sends the page view hit within the
 * EXISTING session, preserving the original traffic source (organic, paid,
 * social, etc.) across the entire visit.
 *
 * CONSENT-AWARE: index.html and CookieConsent grant analytics by default and
 * this function honours a stored explicit analytics opt-out. It does not change
 * the separate opt-in requirement for advertising measurement.
 */
// ---------------------------------------------------------------------------
// Audio player tracking
// ---------------------------------------------------------------------------

/**
 * Fired when a user interacts with the AudioPlayer component.
 * Creates GA4 custom events: audio_play, audio_pause, audio_stop, audio_complete,
 * audio_speed_change, audio_mini_player_shown, audio_mini_player_dismissed
 */
export function trackAudio(params: {
  /** Type of interaction */
  action:
    | "play"
    | "pause"
    | "stop"
    | "complete"
    | "speed_change"
    | "mini_player_shown"
    | "mini_player_dismissed";
  /** Blog post slug or page identifier */
  post_slug: string;
  /** Blog post title (truncated to 100 chars) */
  post_title: string;
  /** Current playback speed, e.g. 0.75, 1.15, 1.5 */
  speed?: number;
  /** Progress percentage at time of event (0-100) */
  progress_pct?: number;
}): void {
  gtagEvent(`audio_${params.action}`, {
    post_slug: params.post_slug,
    post_title: params.post_title.slice(0, 100),
    playback_speed: params.speed ?? null,
    progress_pct: params.progress_pct ?? null,
  });
}

// ---------------------------------------------------------------------------
// Blog post engagement tracking
// ---------------------------------------------------------------------------

/**
 * Fired at scroll depth milestones (25, 50, 75, 90, 100%) on blog posts.
 * Creates a GA4 custom event: scroll_depth
 * Dimensions: depth_pct, post_slug, post_title
 */
export function trackScrollDepth(params: {
  /** Scroll depth milestone: 25 | 50 | 75 | 90 | 100 */
  depth_pct: 25 | 50 | 75 | 90 | 100;
  post_slug: string;
  post_title: string;
}): void {
  gtagEvent("scroll_depth", params);
}

// ---------------------------------------------------------------------------
// useScrollDepth hook (Session 43)
// ---------------------------------------------------------------------------

/**
 * React hook that:
 *  1. Fires GA4 scroll_depth milestone events at 25 / 50 / 75 / 90 / 100%.
 *  2. Returns the current scroll depth percentage as a reactive number so that
 *     CTA onClick handlers can attach it as a dimension (scroll_depth_at_click).
 *
 * Usage:
 *   const scrollDepth = useScrollDepth({ page_slug: "case-study-fintech", page_title: "FinTech Case Study" });
 *   // then in a CTA onClick:
 *   trackOutboundClick({ ..., scroll_depth_at_click: scrollDepth });
 *
 * @param page_slug  Slug or identifier for the current page (used in GA4 events)
 * @param page_title Human-readable page title (used in GA4 events)
 */
export function useScrollDepth(params: {
  page_slug: string;
  page_title: string;
}): number {
  const [currentDepth, setCurrentDepth] = useState(0);
  const firedRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    // Reset on slug change
    firedRef.current = new Set();
    setCurrentDepth(0);
  }, [params.page_slug]);

  useEffect(() => {
    const milestones = [25, 50, 75, 90, 100];

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;

      const pct = Math.min(100, Math.round((scrollTop / docHeight) * 100));
      setCurrentDepth(pct);

      for (const m of milestones) {
        if (pct >= m && !firedRef.current.has(m)) {
          firedRef.current.add(m);
          gtagEvent("scroll_depth", {
            depth_pct: m,
            post_slug: params.page_slug,
            post_title: params.page_title,
          });
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    // Fire once immediately in case the page is short
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [params.page_slug, params.page_title]);

  return currentDepth;
}

/**
 * Fired when a user clicks a share button on a blog post.
 * Creates a GA4 custom event: blog_share_click
 * Dimensions: platform, post_slug, post_title
 */
export function trackBlogShare(params: {
  /** Social platform or share method: "linkedin" | "twitter" | "whatsapp" | "copy_link" | "follow" | "google_preferred" */
  platform: string;
  post_slug: string;
  post_title: string;
}): void {
  gtagEvent("blog_share_click", params);
}

/**
 * Fired when a user clicks a related post card at the bottom of a blog post.
 * Creates a GA4 custom event: related_post_click
 * Dimensions: from_slug, to_slug, position
 */
export function trackRelatedPostClick(params: {
  /** Slug of the current post */
  from_slug: string;
  /** Slug of the post being navigated to */
  to_slug: string;
  /** 1-based position of the card in the related posts grid */
  position: number;
  /** Related-content surface that produced the click. */
  placement?: "bottom" | "mid-article" | "related_ai_insights_carousel";
}): void {
  gtagEvent("related_post_click", params);
}

/**
 * Fired when a reader opens a related breaking-news item at the end of an article.
 * Creates a GA4 custom event: related_news_click.
 */
export function trackRelatedNewsClick(params: {
  from_slug: string;
  to_slug: string;
  position: number;
}): void {
  gtagEvent("related_news_click", params);
}

/**
 * Fired when a user clicks a promoBox CTA in a blog post.
 * Creates a GA4 custom event: blog_cta_click
 * Dimensions: cta_label, destination, post_slug
 */
export function trackBlogCtaClick(params: {
  cta_label: string;
  destination: string;
  post_slug: string;
}): void {
  gtagEvent("blog_cta_click", params);
}

/**
 * Records a document-download intent from an article. This measures the
 * selection of an asset, not whether it was read, completed, or acted upon.
 */
export function trackBlogAssetDownload(params: {
  asset_name: string;
  asset_url: string;
  post_slug: string;
  placement: "post_introduction" | "post_body" | "post_footer";
}): void {
  consentedGtagEvent("blog_asset_download", params);
}

/**
 * Records the consent-aware, non-PII interaction stages for a root-guide lead
 * magnet. This measures resource-request intent and successful delivery, not a
 * commercial outcome or marketing consent.
 */
export function trackGuideLeadMagnet(params: {
  guide_slug: string;
  action:
    | "form_start"
    | "form_submit"
    | "form_success"
    | "form_error"
    | "asset_download";
}): void {
  consentedGtagEvent("guide_lead_magnet", {
    ...params,
    ...getFormAttribution(),
  });
}

/**
 * Records aggregate newsletter form progress without sending an email address
 * or consent text to analytics. Marketing consent itself is stored only by the
 * subscription API after a reader has actively ticked the visible checkbox.
 */
export function trackNewsletterSubscription(params: {
  post_slug: string;
  action:
    | "form_start"
    | "consent_checked"
    | "form_submit"
    | "form_success"
    | "form_error";
}): void {
  consentedGtagEvent("newsletter_subscription", {
    ...params,
    form_location: "blog_news_article",
    ...getFormAttribution(),
  });
}

// ---------------------------------------------------------------------------
// Referral and affiliate conversion-intent tracking (Session 131)
// ---------------------------------------------------------------------------

/**
 * Fires when a visitor selects the canonical Manus referral CTA in a blog post.
 * This records referral intent, not a completed Manus registration or purchase.
 */
export function trackManusReferralClick(params: {
  post_slug: string;
  placement: "post_hero_promo";
  cta_text: string;
  scroll_depth_at_click?: number;
}): void {
  consentedGtagEvent("manus_referral_click", {
    ...params,
    referral_program: "manus",
  });
}

/**
 * Fires when a visitor follows an Amazon UK affiliate resource link.
 * This records an outbound affiliate click, not a confirmed qualifying purchase.
 */
export function trackAmazonAffiliateClick(params: {
  product_title: string;
  product_asin: string;
  product_category: string;
  post_slug: string;
  link_location:
    | "inline_mid_article"
    | "recommended_resources"
    | "arabic_recommended_resources";
}): void {
  consentedGtagEvent("amazon_affiliate_click", {
    ...params,
    affiliate_network: "amazon_uk",
  });
}

/**
 * Fired when a user clicks an inline tool cross-link inside a blog post body.
 * Creates a GA4 custom event: blog_tool_link_click
 * Dimensions: tool_href, post_slug
 */
export function trackBlogToolLinkClick(params: {
  tool_href: string;
  post_slug: string;
}): void {
  gtagEvent("blog_tool_link_click", params);
}

/**
 * Fired when a reader changes the risk-profile lens in an in-article
 * comparison. This captures the selected delegation level only-never any
 * account, payment, credential, or other personal data.
 */
export function trackBlogComparisonRiskProfile(params: {
  post_slug: string;
  comparison_name: string;
  risk_profile: "research_only" | "connected_apps" | "payments";
}): void {
  consentedGtagEvent("blog_comparison_risk_profile", params);
}

// ---------------------------------------------------------------------------
// AI Tools Hub tracking
// ---------------------------------------------------------------------------

/**
 * Fired when a user clicks a tool card on the AI Tools Hub page.
 * Creates a GA4 custom event: tools_hub_tool_click
 * Dimensions: tool_name, tool_href
 */
export function trackToolsHubClick(params: {
  tool_name: string;
  tool_href: string;
}): void {
  gtagEvent("tools_hub_tool_click", params);
}

// ---------------------------------------------------------------------------
// Header / navigation CTA tracking
// ---------------------------------------------------------------------------

/**
 * Fired when a user clicks the main header CTA button (Free AI Visibility Score).
 * Creates a GA4 custom event: header_cta_click
 * Dimensions: cta_label, source ("desktop" | "mobile")
 */
export function trackHeaderCtaClick(params: {
  cta_label: string;
  source: "desktop" | "mobile";
}): void {
  gtagEvent("header_cta_click", params);
}

/**
 * Fired when a user clicks a link inside the desktop Services or Resources dropdown.
 * Creates a GA4 custom event: desktop_nav_click
 * Dimensions: nav_label, nav_href, nav_section
 */
export function trackDesktopNavClick(params: {
  nav_label: string;
  nav_href: string;
  nav_section: "services" | "resources" | "free_tools" | "static";
}): void {
  gtagEvent("desktop_nav_click", params);
}

// ---------------------------------------------------------------------------
// Tool page view tracking
// ---------------------------------------------------------------------------

/**
 * Fired once on mount for each free tool page.
 * Creates a GA4 custom event: tool_page_view
 * Dimensions: tool_page
 */
export function trackToolPageView(tool_page: string): void {
  gtagEvent("tool_page_view", { tool_page });
}

// ---------------------------------------------------------------------------
// SPA page view tracking
// ---------------------------------------------------------------------------

export function trackPageView(pagePath: string, pageTitle?: string) {
  // Use gtag("event", "page_view") - NOT gtag("config") - to avoid creating
  // a new session on every SPA navigation. gtag("config") resets the session
  // and clears traffic source attribution; gtag("event") sends the hit within
  // the existing session, preserving organic/paid/social attribution.
  gtagEvent("page_view", {
    page_path: pagePath,
    page_title:
      pageTitle || (typeof document === "undefined" ? "" : document.title),
    page_location: getPublicPageUrl(),
  });
}

// ---------------------------------------------------------------------------
// Form tracking
// ---------------------------------------------------------------------------

/**
 * Fired when a user focuses the first field of a form (form engagement start).
 * Creates a GA4 custom event: form_start
 * Dimensions: form_name, form_location
 */
export function trackFormStart(params: {
  form_name: string;
  form_location: string;
}): void {
  trackFormLifecycleEvent("form_start", params);
}

/**
 * Fired when a user submits a form (button click / mutate called).
 * Creates a GA4 custom event: form_submit
 * Dimensions: form_name, form_location
 */
export function trackFormSubmit(params: {
  form_name: string;
  form_location: string;
}): void {
  trackFormLifecycleEvent("form_submit", params);
}

/**
 * Fired when a form submission succeeds (server returns 2xx / onSuccess fires).
 * Creates a GA4 custom event: form_success
 * Dimensions: form_name, form_location
 */
export function trackFormSuccess(params: {
  form_name: string;
  form_location: string;
}): void {
  trackFormLifecycleEvent("form_success", params);
}

/**
 * Fired when a form submission fails (server error / onError fires).
 * Creates a GA4 custom event: form_error
 * Dimensions: form_name, form_location, error_message
 */
export function trackFormError(params: {
  form_name: string;
  form_location: string;
  error_message?: string;
}): void {
  trackFormLifecycleEvent("form_error", params);
}

/**
 * Fired when a multi-step form completes a step.
 * Creates a GA4 custom event: form_step_complete
 * Dimensions: form_name, form_location, step_number, step_name
 */
export function trackFormStepComplete(params: {
  form_name: string;
  form_location: string;
  step_number: number;
  step_name: string;
}): void {
  trackFormLifecycleEvent("form_step_complete", params);
}

/**
 * Records aggregate AEO London conversion-path activity. The contract
 * intentionally excludes email, company, URL, budget and free-text answers.
 */
export function trackAeoLondonConversion(params: {
  action:
    | "cta_click"
    | "booking_click"
    | "form_start"
    | "form_submit"
    | "form_success"
    | "form_error"
    | "sticky_click"
    | "score_prompt_shown"
    | "score_click"
    | "exit_intent_shown"
    | "exit_score_click"
    | "exit_email_submit"
    | "exit_email_success"
    | "exit_email_error";
  placement: string;
}): void {
  consentedGtagEvent("aeo_london_conversion", {
    action: params.action,
    placement: params.placement,
    page_slug: "aeo_agency_london",
    ...getFormAttribution(),
  });
}

/**
 * Fired when an OTP verification step is successfully completed.
 * Creates a GA4 custom event: form_otp_verified
 * Dimensions: form_name, form_location
 */
export function trackFormOtpVerified(params: {
  form_name: string;
  form_location: string;
}): void {
  trackFormLifecycleEvent("form_otp_verified", params);
}

/**
 * Fired when a blog search is submitted.
 * Creates a GA4 custom event: blog_search
 * Dimensions: search_query
 */
export function trackBlogSearch(params: { search_query: string }): void {
  gtagEvent("blog_search", params);
}

// ---------------------------------------------------------------------------
// Outbound link click tracking (Session 42)
// ---------------------------------------------------------------------------
/**
 * Fired when a user clicks any outbound link to an external domain.
 * Creates a GA4 custom event: outbound_link_click
 * Dimensions: link_url, link_text, link_location
 *
 * This is the generic fallback; for social platforms use trackOutboundSocialClick.
 */
export function trackOutboundClick(params: {
  /** Full destination URL */
  link_url: string;
  /** Visible anchor text or aria-label */
  link_text: string;
  /** Where on the page the link appeared, e.g. "blog_cta", "contact_page", "footer" */
  link_location: string;
  /**
   * Scroll depth percentage at the moment of click (0-100).
   * Attach this to correlate reading depth with CTA conversions.
   * Obtained from the useScrollDepth() hook.
   */
  scroll_depth_at_click?: number;
}): void {
  gtagEvent("outbound_link_click", {
    ...params,
    event_category: "outbound",
  });
}

// ---------------------------------------------------------------------------
// Email link click tracking (Session 42)
// ---------------------------------------------------------------------------
/**
 * Fired when a user clicks a mailto: link.
 * Creates a GA4 custom event: email_click
 * Dimensions: email_address, link_location
 */
export function trackEmailClick(params: {
  /** Email address being clicked */
  email_address: string;
  /** Where on the page the link appeared, e.g. "footer", "contact_page", "blog_cta" */
  link_location: string;
  /** Scroll depth percentage at the moment of click (0-100). */
  scroll_depth_at_click?: number;
}): void {
  gtagEvent("email_click", {
    ...params,
    event_category: "contact",
  });
}

// ---------------------------------------------------------------------------
// CRO micro-conversion events (Session 43)
// ---------------------------------------------------------------------------

/**
 * Fired when a user hovers a CTA element with intent (debounce in component).
 * Creates a GA4 custom event: cta_hover
 * Dimensions: cta_text, cta_location, page_slug
 *
 * Usage: wire to onMouseEnter with a useRef flag to debounce rapid hovers.
 * High hover:click ratio = friction (copy, trust, price).
 */
export function trackCtaHover(params: {
  /** Visible CTA label, e.g. "Book a Free AI Growth Audit" */
  cta_text: string;
  /** Where on the page, e.g. "hero", "blog_cta", "case_study_cta", "sticky_bar" */
  cta_location: string;
  /** Page slug or identifier */
  page_slug: string;
}): void {
  gtagEvent("cta_hover", params);
}

/**
 * Fired when the user's cursor leaves the viewport upward (desktop exit intent).
 * Creates a GA4 custom event: exit_intent
 * Dimensions: page_slug, scroll_depth_at_exit
 *
 * Usage: wire to document mouseleave where e.clientY <= 0, fire once per page load.
 * High exit intent at low scroll depth = above-the-fold problem.
 */
export function trackExitIntent(params: {
  /** Page slug or identifier */
  page_slug: string;
  /** Scroll depth percentage at the moment of exit (from useScrollDepth()) */
  scroll_depth_at_exit: number;
}): void {
  gtagEvent("exit_intent", params);
}

/**
 * Fired once when the sticky bottom CTA bar becomes visible (scrollY > 300).
 * Creates a GA4 custom event: sticky_cta_shown
 * Dimensions: cta_text, page_slug
 *
 * Compare sticky_cta_shown count vs outbound_link_click where link_location = "sticky_cta"
 * to get the true sticky CTA impression-to-click rate.
 */
export function trackStickyCTAShown(params: {
  cta_text: string;
  page_slug: string;
}): void {
  gtagEvent("sticky_cta_shown", params);
}

/**
 * Fired when a user clicks a pricing plan CTA.
 * Creates a GA4 custom event: pricing_plan_click
 * Dimensions: plan_name, plan_price, cta_text
 */
export function trackPricingPlanClick(params: {
  /** Plan tier, e.g. "starter" | "growth" | "enterprise" */
  plan_name: string;
  /** Displayed price string, e.g. "£2,500/mo" */
  plan_price: string;
  /** Visible CTA text, e.g. "Get started" */
  cta_text: string;
}): void {
  gtagEvent("pricing_plan_click", {
    ...params,
    event_category: "cro",
  });
}

/**
 * Fired when a video starts playing on any page.
 * Creates a GA4 custom event: video_play
 * Dimensions: video_title, video_url, page_slug
 */
export function trackVideoPlay(params: {
  video_title: string;
  video_url: string;
  page_slug: string;
}): void {
  gtagEvent("video_play", params);
}

/**
 * Fired when a video plays to completion (onEnded).
 * Creates a GA4 custom event: video_complete
 * Dimensions: video_title, video_url, page_slug
 */
export function trackVideoComplete(params: {
  video_title: string;
  video_url: string;
  page_slug: string;
}): void {
  gtagEvent("video_complete", params);
}

// ---------------------------------------------------------------------------
// A/B testing events (Session 43)
// ---------------------------------------------------------------------------

/**
 * Fired once per page load when a user is exposed to an A/B test variant.
 * Creates a GA4 custom event: ab_test_exposure
 * Dimensions: experiment_id, variant_id, page_slug
 *
 * Always fire this immediately after calling getVariant() in a useEffect.
 * Use with trackAbTestInteraction() to calculate per-variant conversion rates.
 */
export function trackAbTestExposure(params: {
  /** Slug-style experiment identifier, e.g. "hero-cta-copy-aug-2026" */
  experiment_id: string;
  /** Variant the user was assigned to, e.g. "control" | "variant_b" */
  variant_id: string;
  /** Page where the test is running */
  page_slug: string;
}): void {
  gtagEvent("ab_test_exposure", params);
}

/**
 * Fired when a user interacts with a CTA inside an A/B test variant.
 * Creates a GA4 custom event: ab_test_interaction
 * Dimensions: experiment_id, variant_id, interaction_type, page_slug
 *
 * Also fire the standard trackOutboundClick / trackFormSubmit alongside this
 * so the event appears in regular conversion reports with the A/B dimension attached.
 */
export function trackAbTestInteraction(params: {
  experiment_id: string;
  variant_id: string;
  /** Type of interaction, e.g. "cta_click" | "form_submit" | "scroll_50" */
  interaction_type: string;
  page_slug: string;
}): void {
  gtagEvent("ab_test_interaction", params);
}

// ---------------------------------------------------------------------------
// Blog Search Autocomplete tracking
// ---------------------------------------------------------------------------

/**
 * Fired when the search bar receives focus and the dropdown opens.
 * Creates a GA4 custom event: blog_search_open
 * Dimensions: trigger_source, page_path
 */
export function trackBlogSearchOpen(params: {
  trigger_source: "focus" | "click";
  page_path: string;
}): void {
  gtagEvent("blog_search_open", params);
}

/**
 * Fired when a user selects an autocomplete suggestion.
 * Creates a GA4 custom event: blog_search_suggestion_click
 * Dimensions: suggestion_type, suggestion_label, query_at_click, position
 */
export function trackBlogSearchSuggestionClick(params: {
  suggestion_type: "article" | "tag" | "trending" | "recent";
  suggestion_label: string;
  query_at_click: string;
  position: number;
}): void {
  gtagEvent("blog_search_suggestion_click", params);
}

/**
 * Fired when a user submits a search query (Enter key or Search button).
 * Creates a GA4 custom event: blog_search_submit
 * Dimensions: search_query, result_count, input_method
 */
export function trackBlogSearchSubmit(params: {
  search_query: string;
  result_count: number;
  input_method: "enter_key" | "button_click" | "suggestion_enter";
}): void {
  gtagEvent("blog_search_submit", params);
}

/**
 * Fired when a user clears their recent search history.
 * Creates a GA4 custom event: blog_search_history_cleared
 */
export function trackBlogSearchHistoryCleared(): void {
  gtagEvent("blog_search_history_cleared", {});
}

// ---------------------------------------------------------------------------
// Blog share tracking - upgraded typed helpers
// ---------------------------------------------------------------------------

/**
 * Fired when a user clicks any share button in SharePostRow.
 * Replaces the legacy local trackShare() which called window.gtag directly.
 * Creates a GA4 custom event: blog_share_click
 * Dimensions: platform, post_slug, post_title, share_method
 */
export function trackBlogShareClick(params: {
  platform: string;
  post_slug: string;
  post_title: string;
  share_method?: "icon_button" | "native_api" | "keyboard";
}): void {
  gtagEvent("blog_share_click", {
    ...params,
    share_method: params.share_method ?? "icon_button",
  });
}

// ---------------------------------------------------------------------------
// Smart Sales Assistant tracking
// ---------------------------------------------------------------------------

/**
 * Sends only aggregate, non-PII assistant funnel events. Names, email addresses,
 * company names, website URLs and free-text visitor messages are deliberately
 * excluded from this bridge and remain outside GA4/dataLayer payloads.
 */
export function trackSalesAssistantEvent(
  eventName:
    | "chat_triggered_15s"
    | "chat_open"
    | "chat_dismissed"
    | "question_asked"
    | "service_recommended"
    | "lead_captured"
    | "booking_started"
    | "booking_time_selected"
    | "booking_completed"
    | "score_routed",
  params: Record<string, string | number | boolean | undefined>
): void {
  if (!hasAnalyticsMeasurementConsent() || typeof window === "undefined")
    return;
  const safeParams = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined)
  );
  const dataLayer = (
    window as Window & { dataLayer?: Array<Record<string, unknown>> }
  ).dataLayer;
  dataLayer?.push({ event: eventName, ...safeParams });
  gtagEvent(eventName, safeParams);
}

/**
 * Records only aggregate site-search interactions. Query text is intentionally
 * excluded so visitor free text never reaches analytics.
 */
export function trackSiteSearchEvent(
  action: "open" | "search" | "result_opened" | "popular_topic_selected",
  params: Record<string, string | number | boolean | undefined>
): void {
  if (!hasAnalyticsMeasurementConsent() || typeof window === "undefined")
    return;
  const safeParams = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined)
  );
  gtagEvent("site_search", { search_action: action, ...safeParams });
}
